import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, Lock, RefreshCw, Save } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, EmptyState, LoadingState, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { hasCanonicalRole } from "../../../lib/auth";
import {
  addLot,
  fetchLots,
  getLead,
  getUnreadCount,
  updateLead,
  updateLeadAssignments,
  updateLeadStatus,
  updateLot,
} from "../../../services/leadManagementService";
import {
  CREATE_FIELDS,
  INFORMATION_FIELDS,
  LEAD_STATUSES,
  LOCKED_STATUSES,
  LOST_REASONS,
  formFromLead,
  isLockedLead,
  leadKey,
  nextLeadForm,
  payloadFromForm,
  toOptions,
  useLeadLookups,
  validateLeadForm,
  valueLabel,
} from "../leadForm";
import { LeadForm, LostDetails, SelectInput } from "../components/LeadForm";
import { LeadStatusBadge, LeadStatusDialog } from "../components/LeadStatus";
import { ASSIGNMENT_FIELDS, AssigningTechnician, assignmentsOf } from "../components/AssigningTechnician";
import { LeadAttachments, toAttachment } from "../components/LeadAttachments";
import { InvoiceLotsCard, draftLot, lotFromApi } from "../components/InvoiceLotsCard";

const LEADS_PATH = "/crm/lead-management/leads";
export const TABS = ["information", "caseIndex", "activities", "messages", "quotations"];

/** Tabs with a page of their own, by URL segment; the rest live on the detail page. */
const TAB_PAGES = { caseIndex: "case-index", activities: "activities", messages: "messages" };

export const openLeadTab = (navigate, leadId, tab, lead = null) =>
  tab === "quotations"
    ? navigate(
        `/sales/quotations/quotation?customerId=${encodeURIComponent(String(lead?.customerId ?? ""))}&leadId=${encodeURIComponent(String(leadId ?? ""))}`,
        { state: { leadId, fromLead: true } }
      )
    : TAB_PAGES[tab]
      ? navigate(`${LEADS_PATH}/${leadId}/${TAB_PAGES[tab]}`)
      : navigate(`${LEADS_PATH}/${leadId}`, { state: { tab } });

/** Everything the update endpoint takes, apart from the status and assignments. */
const UPDATE_FIELDS = [...CREATE_FIELDS.filter((key) => key !== "leadStatus"), ...INFORMATION_FIELDS];

/** The lead's tab strip; shared with the Case Index page. */
export function LeadTabs({ active, onChange, unreadCount = 0 }) {
  const { t } = useTranslation();
  return (
    <div className="sticky top-[-24px] lg:top-[-28px] z-20 flex overflow-x-auto border-b border-(--color-text-border)/65 bg-workspace [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => onChange(tab)}
            aria-current={active === tab ? "page" : undefined}
            className={cn(
              "shrink-0 cursor-pointer whitespace-nowrap border-b-2 px-5 py-3 text-sm font-bold transition flex items-center gap-1.5",
              active === tab ? "border-primary text-primary" : "border-transparent per-file-hint hover:text-foreground"
            )}
          >
            <span>{t(leadKey("tabs", tab))}</span>
            {tab === "messages" && unreadCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white min-w-[18px] h-[18px] leading-none shadow-xs">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>
  );
}

/** Shared, compact identity bar for every tab belonging to a lead. */
export function LeadPageHeader({ lead }) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-3">
      <PageTitleRow back={<PageBackLink to={LEADS_PATH} />}>
        <h1 className={cn(ui.pageTitle, "truncate")}>
          <span>{t("leadManagement.fieldsLeadId")}: {lead.leadId}</span>
          {", "}
          <span>{lead.projectName}</span>
        </h1>
      </PageTitleRow>
    </div>
  );
}

/** One lead: header strip, status, and the detail tabs. */
export function LeadDetailPage() {
  const { t } = useTranslation();
  const { leadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lookups = useLeadLookups();

  const [flash, setFlash] = useState(location.state?.flash ?? "");
  const [lead, setLead] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState(location.state?.tab ?? TABS[0]);

  const [form, setForm] = useState(() => formFromLead());
  const [errors, setErrors] = useState({});
  const [assignments, setAssignments] = useState({});
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [lotErrors, setLotErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [pendingStatus, setPendingStatus] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState("");

  useEffect(() => {
    if (location.state?.flash) navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(() => {
    setLoadError("");
    getUnreadCount(leadId)
      .then((res) => setUnreadCount(res?.unreadCount ?? 0))
      .catch(() => {});
    return getLead(leadId)
      .then((loaded) => {
        setLead(loaded);
        setForm(formFromLead(loaded));
        setAssignments(assignmentsOf(loaded));
        setErrors({});
      })
      .catch((error) => setLoadError(error.message));
  }, [leadId]);

  const loadLots = useCallback(() => {
    setLotsLoading(true);
    return fetchLots(leadId)
      .then((rows) => setLots(rows.length ? rows.map(lotFromApi) : [draftLot()]))
      .catch((error) => setSaveError(error.message))
      .finally(() => setLotsLoading(false));
  }, [leadId]);

  useEffect(() => {
    load();
    loadLots();
  }, [load, loadLots]);

  const locked = isLockedLead(lead);
  // Only a Sales Manager assigns people to a lead.
  const canAssign = !locked && hasCanonicalRole("Sales_Manager");
  const currency = lookups.currencies.find((row) => row.id === lead?.projectValueMetric)?.label;

  const change = (key, value) => {
    setSaveError("");
    setFlash("");
    setErrors((current) => ({ ...current, [key]: "" }));
    setForm((current) => nextLeadForm(current, key, value));
  };

  const changeAssignment = (key, value) => {
    setSaveError("");
    setFlash("");
    setAssignments((current) => ({ ...current, [key]: value }));
  };

  const changeLot = (key, field, value) => {
    setSaveError("");
    setFlash("");
    setLotErrors((current) => ({ ...current, [key]: "" }));
    setLots((current) => current.map((lot) => (lot.key === key ? { ...lot, [field]: value, dirty: true } : lot)));
  };

  /**
   * One Save for the page: the lead's details, its assignments (Sales Manager
   * only), and every changed lot. A lot is optional: left empty it is not sent,
   * but one with only a date or only a value is flagged.
   */
  const save = async () => {
    const found = validateLeadForm(form);
    const isFilled = (lot) => lot.plannedInvoiceDate !== "" && lot.lotValue !== "";
    const isEmpty = (lot) => lot.plannedInvoiceDate === "" && lot.lotValue === "";
    const foundLots = Object.fromEntries(
      lots.filter((lot) => !isFilled(lot) && !isEmpty(lot)).map((lot) => [lot.key, t("leadManagement.lotsRequired")])
    );
    const changedLots = lots.filter((lot) => lot.dirty && isFilled(lot));
    setErrors(found);
    setLotErrors(foundLots);
    if (Object.keys(found).length || Object.keys(foundLots).length) {
      setSaveError(t("leadManagement.errorsFixFields"));
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      // The lead keeps its attachments as one list: those on file plus any picked since.
      const added = await Promise.all(pendingFiles.map(toAttachment));
      await updateLead(leadId, {
        ...payloadFromForm(form, UPDATE_FIELDS),
        attachments: [...(lead.attachments ?? []), ...added],
      });
      if (canAssign) {
        await updateLeadAssignments(
          leadId,
          // An unassigned role goes as null, which clears it.
          Object.fromEntries(ASSIGNMENT_FIELDS.map(({ key }) => [key, assignments[key] ? Number(assignments[key]) : null]))
        );
      }
      for (const lot of changedLots) {
        const body = { plannedInvoiceDate: lot.plannedInvoiceDate, lotValue: Number(lot.lotValue), isInvoiced: lot.isInvoiced };
        await (lot.lotId ? updateLot(lot.lotId, body) : addLot(leadId, body));
      }
      setPendingFiles([]);
      setFlash(t("leadManagement.updated"));
      await Promise.all([load(), loadLots()]);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const applyStatus = async (change) => {
    setStatusBusy(true);
    setStatusError("");
    try {
      await updateLeadStatus(leadId, change);
      setPendingStatus("");
      setFlash(t("leadManagement.statusUpdated", { status: valueLabel(change.leadStatus) }));
      await load();
    } catch (error) {
      setStatusError(error.message);
    } finally {
      setStatusBusy(false);
    }
  };

  /** A locking status is confirmed first; the rest are saved as picked. */
  const pickStatus = (status) => {
    setFlash("");
    setStatusError("");
    if (LOCKED_STATUSES.includes(status)) setPendingStatus(status);
    else applyStatus({ leadStatus: status });
  };

  if (loadError) {
    return (
      <AppLayout className="space-y-5">
        <div className="mb-5"><PageBackLink to={LEADS_PATH} /></div>
        <ContentCard bodyClassName="p-4 sm:p-6 text-center">
          <p className="text-sm font-bold text-foreground">{t("leadManagement.notFoundTitle")}</p>
          <p className="mt-1 text-sm per-file-hint">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="mt-4 inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-(--color-text-border) px-4 text-sm font-semibold text-foreground transition hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            {t("leadManagement.retry")}
          </button>
        </ContentCard>
      </AppLayout>
    );
  }

  if (!lead) {
    return (
      <AppLayout className="space-y-5">
        <div className="mb-5"><PageBackLink to={LEADS_PATH} /></div>
        <ContentCard>
          <LoadingState message={t("leadManagement.loading")} />
        </ContentCard>
      </AppLayout>
    );
  }

  const statusOptions = toOptions(LEAD_STATUSES.filter((status) => status !== lead.leadStatus));
  const statusControl = (id) => (
    <div className="flex items-center gap-2">
      <LeadStatusBadge status={lead.leadStatus} />
      {locked ? null : (
        <div className="w-36">
          <SelectInput
            id={id}
            value=""
            options={statusOptions}
            placeholder={statusBusy ? t("leadManagement.saving") : t("leadManagement.changeStatus")}
            disabled={statusBusy}
            onChange={pickStatus}
            className="h-8 rounded-lg px-2.5 text-xs"
          />
        </div>
      )}
    </div>
  );

  return (
    <AppLayout className="space-y-5">

      <LeadPageHeader lead={lead} />

      {flash ? <p className="text-[12px] font-semibold text-emerald-700">{flash}</p> : null}
      {statusError && !pendingStatus ? <p className="text-[12px] font-semibold text-rose-600">{statusError}</p> : null}
      {locked ? (
        <AlertBanner type="info" className="mb-0 flex items-center gap-2">
          <Lock className="h-4 w-4 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {t("leadManagement.lockedBanner", { status: valueLabel(lead.leadStatus) })}
            {lead.lostReason ? (
              <strong className="ml-1">
                {t("leadManagement.lockedBannerReason", { reason: valueLabel(lead.lostReason) })}
              </strong>
            ) : null}
            {lead.lostNote ? (
              <span className="ml-1">&middot; {t("leadManagement.lockedBannerNote", { note: lead.lostNote })}</span>
            ) : null}
          </span>
        </AlertBanner>
      ) : null}

      <LeadTabs
        active={tab}
        unreadCount={unreadCount}
        onChange={(next) =>
          next === "quotations" || TAB_PAGES[next] ? openLeadTab(navigate, leadId, next, lead) : setTab(next)
        }
      />

      {tab === "information" ? (
        <div className="space-y-5">
          {lookups.error ? <AlertBanner type="error" className="mb-0">{lookups.error}</AlertBanner> : null}

          <ContentCard
            title={t("leadManagement.tabsInformation")}
            action={
              <div className="ml-auto flex items-center">{statusControl("lead-changeStatus")}</div>
            }
            bodyClassName="p-4 sm:p-6"
          >
            <LeadForm
              mode="edit"
              form={form}
              errors={errors}
              lookups={lookups}
              lead={lead}
              disabled={locked || saving}
              onChange={change}
            />
            <div className="mt-5">
              <LostDetails lead={lead} />
            </div>
          </ContentCard>

          <AssigningTechnician lead={lead} values={assignments} editable={canAssign} onChange={changeAssignment} />
          <InvoiceLotsCard
            lots={lots}
            loading={lotsLoading}
            editable={!locked}
            currency={currency}
            balance={lead.balanceAmount}
            errors={lotErrors}
            onChange={changeLot}
            onAdd={() => setLots((current) => [...current, draftLot()])}
          />
          <LeadAttachments
            attachments={lead.attachments}
            pending={pendingFiles}
            editable={!locked}
            onAdd={(item) => setPendingFiles((current) => [...current, item])}
            onRemove={(key) => setPendingFiles((current) => current.filter((item) => item.key !== key))}
          />

          {locked ? null : (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              {saveError ? <p className="text-[12px] font-semibold text-rose-600 sm:mr-auto">{saveError}</p> : null}
              <button type="button" onClick={save} disabled={saving} className={cn(ui.primaryBtn, "px-6")}>
                <Save className="h-4 w-4" />
                {saving ? t("leadManagement.saving") : t("leadManagement.saveChanges")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <ContentCard>
          <EmptyState icon={Clock} title={t(leadKey("tabs", tab))} message={t("leadManagement.tabsComingSoon")} />
        </ContentCard>
      )}

      {pendingStatus ? (
        <LeadStatusDialog
          key={pendingStatus}
          status={pendingStatus}
          lostReasons={LOST_REASONS}
          busy={statusBusy}
          error={statusError}
          onCancel={() => {
            setPendingStatus("");
            setStatusError("");
          }}
          onConfirm={applyStatus}
        />
      ) : null}
    </AppLayout>
  );
}
/*
              <span className="ml-1">· {t("leadManagement.lockedBannerNote", { note: lead.lostNote })}</span>
*/
export default LeadDetailPage;
